import {
  Briefcase,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Headset,
  Inbox,
  Keyboard,
  LifeBuoy,
  ListOrdered,
  Lock,
  MessageCircle,
  MessageSquareWarning,
  Mic,
  Phone,
  PhoneCall,
  PhoneIncoming,
  Plug,
  PlusCircle,
  Presentation,
  Puzzle,
  Settings,
  Tag,
  Target,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  GameMissionKey,
  MissionKey,
  PlayableKey,
} from "../domain/training/missions";
import type { MissionState, MissionView } from "../domain/training/progress";
import { GAME_SIZES } from "./content";

const ICONS: Record<MissionKey, LucideIcon> = {
  product_pitch: Mic,
  product_pricing: Tag,
  product_addons: PlusCircle,
  product_fit: Puzzle,
  product_objections: MessageSquareWarning,
  product_availability: CalendarCheck,
  product_how_gala: PhoneIncoming,
  product_setup: Settings,
  product_integrations: Plug,
  product_client_gets: Inbox,
  product_admin_assistant: Briefcase,
  ready: Headset,
  golden_hours: Clock,
  read_queue: ListOrdered,
  right_outcome: Target,
  hotkeys: Keyboard,
  crisis: LifeBuoy,
  first_session: PhoneCall,
  ten_calls: Phone,
  first_contact: MessageCircle,
  first_callback: CalendarClock,
  first_demo: Presentation,
  contact_call: UserRound,
  zero_unlogged: ClipboardCheck,
};

const NODE: Record<MissionState, string> = {
  done: "bg-primary text-primary-foreground border-primary",
  in_progress: "bg-accent text-accent-foreground border-primary",
  available: "bg-background text-primary border-primary",
  locked: "bg-muted text-muted-foreground border-border",
};
const PILL: Record<MissionState, string> = {
  done: "bg-primary/15 text-accent-foreground",
  in_progress: "bg-accent text-accent-foreground",
  available: "bg-secondary text-secondary-foreground",
  locked: "bg-muted text-muted-foreground",
};

/**
 * The mission path: a vertical line of mission cards in map order. Game missions open their mini-game; field
 * missions link to where the rep earns them (they complete by themselves).
 */
export const MissionMap = ({
  views,
  onPlay,
  title: mapTitle,
  subtitle,
  headingId = "gavion-training-map",
  lockedHint,
  sizes,
  unavailable,
}: {
  views: readonly MissionView[];
  onPlay: (key: PlayableKey) => void;
  /** Already translated; defaults to « Carte des missions ». */
  title?: string;
  subtitle?: string;
  headingId?: string;
  /** Already translated; defaults to the dialer course's locked hint. */
  lockedHint?: string;
  /** Question counts per game mission (the `%{count}` of the description). */
  sizes?: Partial<Record<PlayableKey, number>>;
  /** Game missions that cannot be played right now (e.g. no catalog): no Play button. */
  unavailable?: ReadonlySet<PlayableKey>;
}) => {
  const t = useTranslate();
  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="mb-3 text-lg font-bold">
        {mapTitle ?? t("gavion.training.map.title")}
      </h2>
      {subtitle && (
        <p className="mb-3 text-sm text-muted-foreground">{subtitle}</p>
      )}
      <ol className="relative space-y-3 before:absolute before:top-4 before:bottom-4 before:left-5 before:w-0.5 before:bg-border">
        {views.map((v) => {
          const key = v.mission.key;
          const Icon =
            v.state === "done"
              ? CheckCircle2
              : v.state === "locked"
                ? Lock
                : ICONS[key];
          const title = t(`gavion.training.missions.${key}.title`);
          const isGame = v.mission.type === "game";
          return (
            <li key={key} className="relative flex gap-3" data-mission={key}>
              <span
                className={cn(
                  "relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border-2",
                  NODE[v.state],
                )}
                aria-hidden="true"
              >
                <Icon className="size-5" />
              </span>
              <article
                aria-label={title}
                className={cn(
                  "min-w-0 flex-1 rounded-xl border bg-card p-3 shadow-sm sm:p-4",
                  v.state === "locked" && "opacity-70",
                  v.state === "done" && "border-primary/40",
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      {t(
                        isGame
                          ? "gavion.training.map.kind_game"
                          : "gavion.training.map.kind_field",
                      )}
                    </p>
                    <h3 className="font-bold">{title}</h3>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 text-xs font-bold",
                      PILL[v.state],
                    )}
                  >
                    {t(`gavion.training.states.${v.state}`)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(`gavion.training.missions.${key}.desc`, {
                    count: isGame
                      ? (sizes?.[key as PlayableKey] ??
                        GAME_SIZES[key as GameMissionKey] ??
                        0)
                      : 0,
                  })}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  <span className="font-bold text-primary">
                    {t("gavion.training.map.reward", { xp: v.mission.xp })}
                  </span>
                  {v.mission.type === "game" && v.mission.perfectBonus > 0 && (
                    <span className="text-muted-foreground">
                      {t("gavion.training.map.bonus", {
                        xp: v.mission.perfectBonus,
                      })}
                    </span>
                  )}
                  {v.bestScore !== null && (
                    <span>
                      {t("gavion.training.map.best", { score: v.bestScore })}
                    </span>
                  )}
                  {v.attempts > 0 && (
                    <span className="text-muted-foreground">
                      {t("gavion.training.map.attempts", {
                        smart_count: v.attempts,
                      })}
                    </span>
                  )}
                  {!isGame && v.target > 1 && v.state !== "done" && (
                    <span className="font-semibold">
                      {t("gavion.training.map.progress", {
                        count: v.count,
                        target: v.target,
                      })}
                    </span>
                  )}
                </div>
                {!isGame && v.target > 1 && v.state !== "done" && (
                  <div
                    role="progressbar"
                    aria-label={title}
                    aria-valuemin={0}
                    aria-valuemax={v.target}
                    aria-valuenow={v.count}
                    className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-primary/15"
                  >
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{
                        width: `${Math.round((v.count / v.target) * 100)}%`,
                      }}
                    />
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {isGame ? (
                    v.state === "locked" ? (
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Lock className="size-3.5" aria-hidden="true" />
                        {lockedHint ?? t("gavion.training.map.locked_hint")}
                      </p>
                    ) : unavailable?.has(key as PlayableKey) ? (
                      <p className="text-xs text-muted-foreground">
                        {t("gavion.training.product.catalog_unavailable")}
                      </p>
                    ) : (
                      <Button
                        size="sm"
                        variant={v.state === "done" ? "outline" : "default"}
                        onClick={() => onPlay(key as PlayableKey)}
                        aria-label={`${t(v.attempts > 0 ? "gavion.training.map.replay" : "gavion.training.map.play")} — ${title}`}
                      >
                        {t(
                          v.attempts > 0
                            ? "gavion.training.map.replay"
                            : "gavion.training.map.play",
                        )}
                      </Button>
                    )
                  ) : v.state === "done" ? null : (
                    <>
                      <Button asChild size="sm" variant="outline">
                        <Link
                          to={v.mission.type === "field" ? v.mission.cta : "/"}
                        >
                          {t(`gavion.training.missions.${key}.cta`)}
                        </Link>
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {t("gavion.training.map.auto_hint")}
                      </span>
                    </>
                  )}
                </div>
              </article>
            </li>
          );
        })}
      </ol>
    </section>
  );
};
